import type { GoalView } from "@cx/contracts";
import {
  formatBRL,
  formatDateBR,
  formatPct,
  formatQty,
  type GoalMetric,
  type GoalState,
  type Uf,
} from "@cx/domain";

/** Valores que o servidor não soma com segurança (acima de 2^53) chegam null e aparecem como "—". */
export const money = (c: number | null | undefined) => (c === null || c === undefined ? "—" : formatBRL(c));
export const count = (n: number | null | undefined) =>
  n === null || n === undefined ? "—" : new Intl.NumberFormat("pt-BR").format(n);
export const qty = (q4: number | null | undefined) => (q4 === null || q4 === undefined ? "—" : formatQty(q4));
export const pct = (p4: number | null | undefined) => (p4 === null || p4 === undefined ? "—" : formatPct(p4));

/** Query string sem as chaves vazias; listas viram ids separados por vírgula. */
export function reportQuery(
  params: Record<string, string | number | readonly string[] | null | undefined>,
): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    if (Array.isArray(v)) {
      if (v.length) q.set(k, v.join(","));
    } else q.set(k, String(v));
  }
  const s = q.toString();
  return s ? `?${s}` : "";
}

/** "01/10/2026 a 03/10/2026" (um dia só: a data). */
export const periodLabel = (p: { from: string; to: string }) =>
  p.from === p.to ? formatDateBR(p.from) : `${formatDateBR(p.from)} a ${formatDateBR(p.to)}`;

/** "03/10" para o eixo dos gráficos diários. */
export const dayMonth = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
/** "10/2026" para os eixos mensais. */
export const monthYear = (month: string) => `${month.slice(5, 7)}/${month.slice(0, 4)}`;
export const hourLabel = (h: number) => `${String(h).padStart(2, "0")}h`;

const MONTHS = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
export const shortMonth = (m: number) => MONTHS[m - 1] ?? "?";

export const GOAL_METRIC_LABEL: Record<GoalMetric, string> = {
  revenue: "Valor vendido",
  orders: "Quantidade de pedidos",
  items: "Itens vendidos",
  avg_ticket: "Ticket médio",
  new_customers: "Clientes novos",
};
export const GOAL_STATE_LABEL: Record<GoalState, string> = {
  current: "Em andamento",
  future: "Futura",
  past: "Encerrada",
};

/** Alvo e realizado na unidade da métrica (centavos, q4 ou contagem). */
export function goalValue(metric: GoalMetric, v: number | null): string {
  if (metric === "revenue" || metric === "avg_ticket") return money(v);
  if (metric === "items") return qty(v);
  return count(v);
}

/**
 * Situação do ritmo da meta: atingida, no ritmo (progresso ≥ tempo decorrido) ou abaixo do ritmo. Ticket médio não
 * acumula com o tempo: só compara com o alvo.
 */
export function goalPace(g: Pick<GoalView, "metric" | "progressPct4" | "elapsedPct4" | "state">): {
  label: string;
  tone: "success" | "warning" | "danger" | "secondary";
} {
  const p = g.progressPct4;
  if (p !== null && p >= 1_000_000) return { label: "Atingida", tone: "success" };
  if (g.state === "future") return { label: "Não iniciada", tone: "secondary" };
  if (g.state === "past") return { label: "Não atingida", tone: "danger" };
  if (p === null) return { label: "Sem dados", tone: "secondary" };
  const expected = g.metric === "avg_ticket" ? 1_000_000 : g.elapsedPct4;
  return p >= expected
    ? { label: "No ritmo", tone: "success" }
    : { label: "Abaixo do ritmo", tone: "warning" };
}

/** Largura da barra de progresso (0..100, sem passar de 100). */
export const progressWidth = (p4: number | null) =>
  p4 === null ? 0 : Math.max(0, Math.min(100, p4 / 10_000));

/**
 * Grade de UFs (mapa em blocos, aproximando a geografia): [linha, coluna] de cada estado numa grade de 7 colunas.
 */
export const UF_TILES: Record<Uf, [number, number]> = {
  RR: [0, 2],
  AP: [0, 3],
  AM: [1, 1],
  PA: [1, 2],
  MA: [1, 3],
  CE: [1, 4],
  RN: [1, 5],
  AC: [2, 0],
  RO: [2, 1],
  TO: [2, 2],
  PI: [2, 3],
  PB: [2, 4],
  PE: [2, 5],
  MT: [3, 1],
  GO: [3, 2],
  BA: [3, 3],
  SE: [3, 4],
  AL: [3, 5],
  MS: [4, 1],
  DF: [4, 2],
  MG: [4, 3],
  ES: [4, 4],
  PR: [5, 1],
  SP: [5, 2],
  RJ: [5, 3],
  SC: [6, 1],
  RS: [7, 1],
};

/** Intensidade da cor do bloco (0..1) pela participação no maior valor; null/zero = sem cor. */
export function tileShare(value: number | null, max: number | null): number {
  if (!value || !max || max <= 0) return 0;
  return Math.max(0.12, Math.min(1, value / max));
}
